import React, { useState, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import Button from '@atlaskit/button'
import TextField from '@atlaskit/textfield'
import Select from '@atlaskit/select'
import AddIcon from '@atlaskit/icon/glyph/add'
import SearchIcon from '@atlaskit/icon/glyph/search'
import DocumentsIcon from '@atlaskit/icon/glyph/documents'
import { selectCurrentLibraryFolder } from '../../store/slices/librarySlice'
import { LibraryScenarioCard } from './LibraryScenarioCard'
import { ScenarioPanelSkeleton } from '../common/FolderBrowserSkeleton'
import { prefetchUsers } from '../../utils/userCache'

/**
 * Shared compact styles for all Select dropdowns in the filter bar.
 * Keeps all filter controls at a consistent 30px height with 13px font.
 */
const compactSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '30px',
    height: '30px',
    fontSize: '13px'
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px'
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: '13px'
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '30px'
  })
}

/**
 * SORTABLE LIBRARY SCENARIO WRAPPER
 *
 * Wraps LibraryScenarioCard with sortable drag-and-drop functionality.
 */
function SortableLibraryScenarioCard({
  scenario,
  onClick,
  onEdit,
  onMove,
  onDelete,
  onClone,
  isDragDisabled
}) {
  // Use prefixed ID to distinguish scenarios from folders in drag events
  const sortableId = `scenario-${scenario.id}`

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: sortableId,
    disabled: isDragDisabled,
    data: {
      type: 'scenario',
      scenario: scenario
    }
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative'
  }

  return (
    <div ref={setNodeRef} style={style}>
      <LibraryScenarioCard
        scenario={scenario}
        onClick={onClick}
        onEdit={onEdit}
        onMove={onMove}
        onDelete={onDelete}
        onClone={onClone}
        dragHandleProps={{ ...attributes, ...listeners }}
        isDragDisabled={isDragDisabled}
        isDragging={isDragging}
      />
    </div>
  )
}

/**
 * DRAGGABLE LIBRARY SCENARIO WRAPPER (move-to-folder only)
 *
 * Used when reordering is not meaningful for the current view ("All Library
 * Scenarios" or any active filter). Cards are draggable so the user can drop
 * them onto a library folder in the sidebar, but they are NOT registered as
 * droppables — so they cannot become a reorder target, and sibling rows do
 * not shuffle during drag.
 *
 * Mirrors SortableLibraryScenarioCard's drag-id format (`scenario-{id}`) and
 * data payload so the unified drag handler in Library.jsx treats both modes
 * identically for the scenario→folder case.
 */
function DraggableLibraryScenarioCard({
  scenario,
  onClick,
  onEdit,
  onMove,
  onDelete,
  onClone
}) {
  const draggableId = `scenario-${scenario.id}`

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging
  } = useDraggable({
    id: draggableId,
    data: {
      type: 'scenario',
      scenario: scenario
    }
  })

  // Hide the source row while dragging — the DragOverlay (configured in
  // Library) renders the visible drag preview that follows the cursor.
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    position: 'relative'
  }

  return (
    <div ref={setNodeRef} style={style}>
      <LibraryScenarioCard
        scenario={scenario}
        onClick={onClick}
        onEdit={onEdit}
        onMove={onMove}
        onDelete={onDelete}
        onClone={onClone}
        dragHandleProps={{ ...attributes, ...listeners }}
        isDragDisabled={false}
        isDragging={isDragging}
      />
    </div>
  )
}

/**
 * LIBRARY SCENARIO PANEL COMPONENT
 *
 * Right panel of the library browser showing library scenarios (templates).
 *
 * Features:
 * - Displays scenarios for the selected library folder
 * - Shows "All Library Folders" when no folder selected
 * - Inline filter bar: search, priority, sort (single row, Jira-style)
 * - Each scenario shows name, assignee, priority, test case count
 * - Drag-and-drop to reorder scenarios within folder
 * - Click scenario to navigate to scenario detail page
 */
function LibraryScenarioPanel({
  scenarios = [],
  selectedFolderId,
  loading = false,
  onScenarioClick,
  onCreateScenario,
  onEditScenario,
  onDeleteScenario,
  onMoveScenario,
  onCloneScenario,
  onReorderScenarios,
  jiraProjectId
}) {
  const currentFolder = useSelector(selectCurrentLibraryFolder)

  // Search, filter, and sort state
  const [searchQuery, setSearchQuery] = useState('')
  const [priorityFilter, setPriorityFilter] = useState(null)
  const [showToolbar, setShowToolbar] = useState(false)

  // Prefetch all assignee user details when scenarios change
  // This uses bulk API call instead of N individual calls per card
  useEffect(() => {
    const accountIds = scenarios
      .map(s => typeof s.assignee === 'string' ? s.assignee : s.assignee?.accountId)
      .filter(Boolean)
    const uniqueIds = [...new Set(accountIds)]
    if (uniqueIds.length > 0) {
      prefetchUsers(uniqueIds)
    }
  }, [scenarios])

  // Priority filter options
  const priorityOptions = [
    { value: null, label: 'All Priorities' },
    { value: 'Critical', label: 'Critical' },
    { value: 'High', label: 'High' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Low', label: 'Low' }
  ]

  // Helper to get creation date from scenario
  const getCreationValue = (scenario) => {
    const dateValue = scenario.createdOn
    if (dateValue) {
      const date = new Date(dateValue)
      return isNaN(date.getTime()) ? (scenario.id || 0) : date.getTime()
    }
    return scenario.id || 0
  }

  // Filter and sort scenarios
  const filteredScenarios = useMemo(() => {
    let result = [...scenarios]

    // Apply search filter — match by name, description, or scenario key (e.g. "POJ-1")
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter(s =>
        s.scenarioName?.toLowerCase().includes(query) ||
        s.description?.toLowerCase().includes(query) ||
        s.scenarioKey?.toLowerCase().includes(query)
      )
    }

    // Apply priority filter
    if (priorityFilter?.value) {
      result = result.filter(s => s.priority === priorityFilter.value)
    }

    // Apply the view-dependent implicit sort (no user-facing sort picker):
    //   - Inside a folder → sort by orderIndex (drag-and-drop order, folder-scoped).
    //   - "All Library Scenarios" → sort by creation date (newest first) because orderIndex is
    //     per-folder and mixing folders would produce a meaningless order.
    if (currentFolder) {
      result.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))
    } else {
      result.sort((a, b) => getCreationValue(b) - getCreationValue(a))
    }

    return result
  }, [scenarios, searchQuery, priorityFilter, currentFolder])

  // Check if any filters are active (used for button state, count display, clear button)
  const hasActiveFilters = searchQuery.trim() || priorityFilter?.value

  // Reordering is only meaningful when the visible list matches the DB's folder-scoped
  // orderIndex sequence. That requires:
  //   1. A specific folder is selected (orderIndex is per-folder — "All Library Scenarios" mixes folders).
  //   2. No filters are active (filters show a subset, so drag positions would be wrong).
  // Note: this gates *reorder* only. Move-to-folder (dragging a scenario onto a folder
  // in the sidebar) is allowed in every view via the DraggableLibraryScenarioCard branch below.
  const canReorder = !!currentFolder && !hasActiveFilters

  // Clear all filters. Sort is implicit (no user-facing picker) so there's nothing to reset.
  const handleClearFilters = () => {
    setSearchQuery('')
    setPriorityFilter(null)
  }

  // Panel title: folder name when a folder is selected, otherwise the "All Scenarios" view label
  const panelTitle = currentFolder?.name || 'All Scenarios'

  // Get scenario IDs for SortableContext
  const scenarioIds = filteredScenarios.map(s => `scenario-${s.id}`)

  // Loading state - shimmer skeleton mimicking scenario card rows
  if (loading) {
    return <ScenarioPanelSkeleton />
  }

  return (
    <div
      className="library-scenario-panel"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--ds-surface, #FFFFFF)',
        overflow: 'hidden',
        borderTopRightRadius: '8px',
        borderBottomRightRadius: '8px'
      }}
    >
      {/* Panel Header */}
      <div
        className="scenario-panel-header"
        style={{
          padding: '8px 16px',
          borderBottom: '1px solid var(--ds-border, #DFE1E6)'
        }}
      >
        {/* Title Row */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: (showToolbar || hasActiveFilters) ? '12px' : '0'
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <h2 style={{
              font: 'var(--ds-font-body)',
              fontWeight: 600,
              color: 'var(--ds-text, #172B4D)',
              margin: 0
            }}>
              {panelTitle}
            </h2>
            <span style={{
              fontSize: '12px',
              color: 'var(--ds-text-subtlest, #6B778C)'
            }}>
              {hasActiveFilters
                ? `(${filteredScenarios.length} of ${scenarios.length})`
                : `(${scenarios.length})`
              }
            </span>
          </div>

          {/* Filter/Search Toggle Button */}
          <Button
            appearance={(showToolbar || hasActiveFilters) ? 'primary' : 'default'}
            iconBefore={<SearchIcon label="Search and filter" size="small" />}
            onClick={() => setShowToolbar(!showToolbar)}
            isSelected={showToolbar || hasActiveFilters}
          >
            {hasActiveFilters ? 'Filtered' : 'Search'}
          </Button>
        </div>

        {/* Inline Filter & Sort Bar - Single row, Jira-style */}
        {(showToolbar || hasActiveFilters) && (
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {/* Search Input */}
            <div style={{ flex: '1 1 160px', maxWidth: '200px', minWidth: '120px' }}>
              <TextField
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                elemBeforeInput={
                  <div style={{ paddingLeft: '8px', display: 'flex', alignItems: 'center' }}>
                    <SearchIcon label="Search" size="small" primaryColor="var(--ds-text-subtlest, #6B778C)" />
                  </div>
                }
                isCompact
              />
            </div>

            {/* Priority Filter */}
            <div style={{ width: '130px', flexShrink: 0 }}>
              <Select
                options={priorityOptions}
                value={priorityFilter || priorityOptions[0]}
                onChange={(option) => setPriorityFilter(option?.value ? option : null)}
                isCompact
                isClearable={false}
                placeholder="Priority"
                styles={compactSelectStyles}
              />
            </div>

            {/* Clear Filters Button - only visible when filters are active */}
            {hasActiveFilters && (
              <Button
                appearance="subtle"
                onClick={handleClearFilters}
                spacing="compact"
              >
                Clear
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Scenario List with Drag-and-Drop */}
      <div
        className="scenario-list"
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '0'
        }}
      >
        {filteredScenarios.length > 0 ? (
          canReorder ? (
            // Reorder-capable view: SortableContext + verticalListSortingStrategy
            // give the cards sibling-shuffle behavior during drag, and dropping
            // on another scenario triggers reorder via Library CASE 3.
            <SortableContext
              items={scenarioIds}
              strategy={verticalListSortingStrategy}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {filteredScenarios.map(scenario => (
                  <SortableLibraryScenarioCard
                    key={scenario.id}
                    scenario={scenario}
                    onClick={() => onScenarioClick(scenario.id)}
                    onEdit={onEditScenario}
                    onMove={onMoveScenario}
                    onDelete={onDeleteScenario}
                    onClone={onCloneScenario}
                    isDragDisabled={false}
                  />
                ))}
              </div>
            </SortableContext>
          ) : (
            // Move-to-folder-only view ("All Library Scenarios" or active filters):
            // no SortableContext, so scenarios are not droppable on each other
            // (no reorder, no sibling shuffle). Library folder rows in the sidebar
            // are independent useDroppable targets, so dragging onto a folder still
            // routes through Library CASE 1 (moveLibraryScenarioToFolderAsync).
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {filteredScenarios.map(scenario => (
                <DraggableLibraryScenarioCard
                  key={scenario.id}
                  scenario={scenario}
                  onClick={() => onScenarioClick(scenario.id)}
                  onEdit={onEditScenario}
                  onMove={onMoveScenario}
                  onDelete={onDeleteScenario}
                  onClone={onCloneScenario}
                />
              ))}
            </div>
          )
        ) : scenarios.length > 0 ? (
          /* No Results from Filter */
          <div
            className="empty-state"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px',
              textAlign: 'center',
              height: '100%'
            }}
          >
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px'
            }}>
              <SearchIcon
                label="No results"
                size="medium"
                primaryColor="var(--ds-text-subtlest, #6B778C)"
              />
            </div>

            <h3 style={{
              fontSize: '16px',
              fontWeight: 500,
              color: 'var(--ds-text, #172B4D)',
              margin: '0 0 8px 0'
            }}>
              No matching scenarios
            </h3>

            <p style={{
              fontSize: '13px',
              color: 'var(--ds-text-subtlest, #6B778C)',
              margin: '0 0 20px 0',
              maxWidth: '280px'
            }}>
              Try adjusting your search or filter criteria.
            </p>

            <Button
              appearance="subtle"
              onClick={handleClearFilters}
            >
              Clear filters
            </Button>
          </div>
        ) : (
          /* Empty State - No Scenarios */
          <div
            className="empty-state"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px',
              textAlign: 'center',
              height: '100%'
            }}
          >
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px'
            }}>
              <DocumentsIcon
                label="Scenarios"
                size="medium"
                primaryColor="var(--ds-text-subtlest, #6B778C)"
              />
            </div>

            <h3 style={{
              fontSize: '16px',
              fontWeight: 500,
              color: 'var(--ds-text, #172B4D)',
              margin: '0 0 8px 0'
            }}>
              {selectedFolderId === null
                ? 'No library scenarios yet'
                : 'This folder is empty'}
            </h3>

            <p style={{
              fontSize: '13px',
              color: 'var(--ds-text-subtlest, #6B778C)',
              margin: '0 0 20px 0',
              maxWidth: '280px'
            }}>
              {selectedFolderId === null
                ? 'Create reusable test scenario templates that can be imported into releases.'
                : 'Drag scenarios here or create new ones.'}
            </p>

            <Button
              appearance="primary"
              iconBefore={<AddIcon label="Add" size="small" />}
              onClick={onCreateScenario}
            >
              Add Scenario
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export { LibraryScenarioPanel }
